import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Fade,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import { Sync, Store, Add, Link as LinkIcon, Schedule, Refresh } from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';
import FormDialog from '../components/common/FormDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StatusBadge from '../components/common/StatusBadge';
import SearchInput from '../components/common/SearchInput';
import api from '../services/api';

const initialFormData = {
  name: '',
  code: '',
  feedUrl: '',
  feedRefreshInterval: 3600,
  brands: '',
  matchingFeedKey: 'mpn',
  matchingCatalogKey: 'sku',
  matchingNormalization: ['trim', 'uppercase'],
  isActive: true,
};

const RetailsPage = () => {
  const [retails, setRetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRetail, setSelectedRetail] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchRetails = useCallback(async () => {
    try {
      const response = await api.get('/retails');
      setRetails(response.data);
    } catch (err) {
      setError('Error al cargar retails');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRetails();
  }, [fetchRetails]);

  const handleOpenDialog = (retail = null) => {
    if (retail) {
      setFormData({
        name: retail.name,
        code: retail.code,
        feedUrl: retail.feedUrl || '',
        feedRefreshInterval: retail.feedRefreshInterval || 3600,
        brands: retail.brands?.join(', ') || '',
        matchingFeedKey: retail.matchingRule?.feedKey || 'mpn',
        matchingCatalogKey: retail.matchingRule?.catalogKey || 'sku',
        matchingNormalization: retail.matchingRule?.normalization || ['trim', 'uppercase'],
        isActive: retail.isActive,
      });
      setSelectedRetail(retail);
    } else {
      setFormData(initialFormData);
      setSelectedRetail(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setSelectedRetail(null);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        code: formData.code,
        feedUrl: formData.feedUrl,
        feedRefreshInterval: parseInt(formData.feedRefreshInterval),
        brands: formData.brands.split(',').map(b => b.trim()).filter(Boolean),
        matchingRule: {
          feedKey: formData.matchingFeedKey,
          catalogKey: formData.matchingCatalogKey,
          normalization: formData.matchingNormalization,
        },
        isActive: formData.isActive,
      };

      if (selectedRetail) {
        await api.put(`/retails/${selectedRetail.id}`, payload);
        setSuccess('Retail actualizado exitosamente');
      } else {
        await api.post('/retails', payload);
        setSuccess('Retail creado exitosamente');
      }
      handleCloseDialog();
      fetchRetails();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar retail');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/retails/${selectedRetail.id}`);
      setDeleteDialogOpen(false);
      setSelectedRetail(null);
      setSuccess('Retail eliminado exitosamente');
      fetchRetails();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar retail');
    }
  };

  const handleSyncFeed = async (retail) => {
    setSyncing(retail.id);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/feeds/sync/${retail.id}`);
      setSuccess(`Sincronización completada: ${response.data.matched} productos sincronizados`);
      fetchRetails();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al sincronizar feed');
    } finally {
      setSyncing(null);
    }
  };

  const filteredRetails = retails.filter(retail =>
    retail.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    retail.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Nombre', accessor: 'name' },
    { header: 'Código', accessor: 'code' },
    {
      header: 'Feed URL',
      accessor: 'feedUrl',
      render: (value) => value ? (
        <Tooltip title={value} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography
              variant="body2"
              sx={{
                maxWidth: 180,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'primary.main',
              }}
            >
              Configurado
            </Typography>
          </Box>
        </Tooltip>
      ) : (
        <Typography variant="body2" color="text.disabled">
          No configurado
        </Typography>
      ),
    },
    {
      header: 'Última Sync',
      accessor: 'lastFeedSync',
      render: (value) => value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {new Date(value).toLocaleDateString('es-ES', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.disabled">Nunca</Typography>
      ),
    },
    {
      header: 'Estado',
      accessor: 'isActive',
      render: (value) => (
        <StatusBadge status={value ? 'active' : 'inactive'} />
      ),
    },
    {
      header: 'Sync',
      accessor: 'id',
      render: (value, row) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Sync sx={{ animation: syncing === value ? 'spin 1s linear infinite' : 'none' }} />}
          onClick={(e) => {
            e.stopPropagation();
            handleSyncFeed(row);
          }}
          disabled={syncing === value || !row.feedUrl}
          sx={{
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        >
          {syncing === value ? 'Sincronizando...' : 'Sync'}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Retails"
        subtitle="Gestiona los retails y sus feeds de productos"
        actionLabel="Nuevo Retail"
        actionIcon={<Add />}
        onAction={() => handleOpenDialog()}
        badge={retails.length}
        badgeColor="primary"
      >
        <Tooltip title="Refrescar lista">
          <IconButton onClick={fetchRetails}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </PageHeader>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}
      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Box sx={{ mb: 3 }}>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por nombre o código..."
          sx={{ minWidth: 300 }}
        />
      </Box>

      <DataTable
        columns={columns}
        data={filteredRetails}
        loading={loading}
        onEdit={handleOpenDialog}
        onDelete={(retail) => {
          setSelectedRetail(retail);
          setDeleteDialogOpen(true);
        }}
        emptyMessage="No hay retails registrados"
        emptyDescription="Agrega tu primera tienda para comenzar"
        emptyActionLabel="Agregar Retail"
        emptyAction={() => handleOpenDialog()}
      />

      <FormDialog
        open={dialogOpen}
        title={selectedRetail ? 'Editar Retail' : 'Nuevo Retail'}
        subtitle={selectedRetail ? 'Modifica la configuración del retail' : 'Configura una nueva tienda'}
        icon={<Store />}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        loading={saving}
        maxWidth="md"
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              autoFocus
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Código"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
              helperText="Identificador único"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="URL del Feed (XML)"
              value={formData.feedUrl}
              onChange={(e) => setFormData({ ...formData, feedUrl: e.target.value })}
              fullWidth
              placeholder="https://example.com/feed.xml"
              helperText="URL del feed de Google Shopping"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Intervalo de Refresh"
              type="number"
              value={formData.feedRefreshInterval}
              onChange={(e) => setFormData({ ...formData, feedRefreshInterval: e.target.value })}
              fullWidth
              InputProps={{
                endAdornment: <Typography variant="caption" color="text.secondary">segundos</Typography>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Marcas"
              value={formData.brands}
              onChange={(e) => setFormData({ ...formData, brands: e.target.value })}
              fullWidth
              placeholder="HP, Dell, Epson"
              helperText="Separadas por coma"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Reglas de Matching
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Campo del Feed</InputLabel>
              <Select
                value={formData.matchingFeedKey}
                label="Campo del Feed"
                onChange={(e) => setFormData({ ...formData, matchingFeedKey: e.target.value })}
              >
                <MenuItem value="mpn">MPN (g:mpn)</MenuItem>
                <MenuItem value="id">ID (g:id)</MenuItem>
                <MenuItem value="gtin">GTIN (g:gtin)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Campo del Catálogo</InputLabel>
              <Select
                value={formData.matchingCatalogKey}
                label="Campo del Catálogo"
                onChange={(e) => setFormData({ ...formData, matchingCatalogKey: e.target.value })}
              >
                <MenuItem value="sku">SKU</MenuItem>
                <MenuItem value="prodnum">Prodnum</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Retail Activo"
            />
          </Grid>
        </Grid>
      </FormDialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Retail"
        message={`¿Estás seguro de que deseas eliminar el retail "${selectedRetail?.name}"? Esta acción eliminará todas las configuraciones asociadas.`}
        type="delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default RetailsPage;
